<template>
  <div>

    <!-- ════════ FILE UPLOAD (before file selected) ════════ -->
    <div v-if="!hasFile" class="upload-zone">
      <v-file-input
        v-model="file"
        :label="$t('importarProductos.selectFile')"
        prepend-icon="mdi-file-upload-outline"
        accept=".csv,.xlsx,.xls"
        color="primary"
        outlined
        dense
        show-size
        hide-details
        :loading="loadingFile"
        class="upload-input"
        @change="onFileSelected"
      />
      <p class="caption grey--text mt-2 mb-0">
        Formatos aceptados: .csv, .xlsx, .xls
      </p>
    </div>

    <!-- ════════ PARSE ERROR ════════ -->
    <v-alert v-if="parseError" type="error" dense text class="mb-4">
      {{ parseError }}
    </v-alert>

    <!-- ════════ MAPPING WIZARD ════════ -->
    <template v-if="hasFile && !parseError">

      <!-- File header bar -->
      <div class="file-bar mb-4">
        <div class="file-bar-info">
          <v-icon small color="primary" class="mr-1">mdi-file-table-outline</v-icon>
          <span class="file-bar-name">{{ file && file.name }}</span>
          <span class="file-bar-count ml-2">
            {{ allRows.length }} filas · {{ headers.length }} columnas
          </span>
        </div>
        <button class="file-bar-change" @click="resetFile">
          <v-icon x-small class="mr-1">mdi-swap-horizontal</v-icon>
          Cambiar archivo
        </button>
      </div>

      <!-- ── Instruction banner ── -->
      <div
        class="instruction-banner mb-4"
        :class="isAllDone ? 'banner-done' : 'banner-active'"
      >
        <template v-if="isAllDone">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <span><strong>¡Listo!</strong> {{ $t("importarProductos.allFieldsMapped") }}</span>
        </template>
        <template v-else>
          <div class="banner-step-badge">{{ currentStepIdx + 1 }}/{{ fieldSteps.length }}</div>
          <div class="banner-text">
            <span class="banner-verb">Hacé click en el header</span>
            <span class="banner-field" :style="{ color: fieldHex(currentField.key) }">
              {{ fieldLabel(currentField.key) }}
            </span>
            <span v-if="currentField.required" class="banner-req">(requerido)</span>
            <span v-else class="banner-opt">(opcional)</span>
          </div>
        </template>
      </div>

      <!-- ── Table ── -->
      <div class="table-wrapper mb-3">
        <div class="table-scroll">
          <table class="mapping-table" cellspacing="0" cellpadding="0">
            <thead>
              <tr>
                <!-- Row number -->
                <th class="col-num"></th>
                <!-- Data columns -->
                <th
                  v-for="(header, colIdx) in headers"
                  :key="colIdx"
                  class="col-header"
                  :class="headerColClass(colIdx)"
                  :style="headerColStyle(colIdx)"
                  @click="onHeaderClick(colIdx)"
                  @mouseenter="!isAllDone && (hoveredCol = colIdx)"
                  @mouseleave="hoveredCol = null"
                >
                  <div class="col-header-inner">
                    <!-- Field color bar (top) — always rendered to keep height stable -->
                    <div
                      class="col-color-bar"
                      :class="{ 'col-color-bar-hover': !mappedFieldForCol(colIdx) && !isAllDone && hoveredCol === colIdx }"
                      :style="mappedFieldForCol(colIdx)
                        ? { background: fieldHex(mappedFieldForCol(colIdx)) }
                        : { background: 'transparent' }"
                    ></div>

                    <div class="col-header-content">
                      <div class="col-header-row">
                        <span class="col-header-name">{{ header || '(sin nombre)' }}</span>

                        <!-- Mapped badge -->
                        <div
                          v-if="mappedFieldForCol(colIdx)"
                          class="field-tag"
                          :style="{
                            background: fieldHexLight(mappedFieldForCol(colIdx)),
                            color: fieldHex(mappedFieldForCol(colIdx)),
                            borderColor: fieldHex(mappedFieldForCol(colIdx))
                          }"
                        >
                          <span
                            class="field-tag-dot"
                            :style="{ background: fieldHex(mappedFieldForCol(colIdx)) }"
                          ></span>
                          {{ fieldLabel(mappedFieldForCol(colIdx)) }}
                        </div>

                        <!-- Hover hint (unassigned, active step) -->
                        <div
                          v-else-if="!isAllDone && hoveredCol === colIdx"
                          class="field-tag field-tag-hover"
                          :style="{
                            background: fieldHexLight(currentField && currentField.key),
                            color: fieldHex(currentField && currentField.key),
                            borderColor: fieldHex(currentField && currentField.key),
                          }"
                        >
                          <v-icon x-small style="color:inherit; margin-right:2px">mdi-cursor-default-click</v-icon>
                          Asignar
                        </div>
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rowIdx) in previewRows"
                :key="rowIdx"
                class="data-row"
                :class="rowIdx % 2 === 0 ? 'row-even' : 'row-odd'"
              >
                <td class="col-num">{{ rowIdx + 1 }}</td>
                <td
                  v-for="(cell, colIdx) in row"
                  :key="colIdx"
                  class="data-cell"
                  :class="dataCellClass(colIdx)"
                  :style="dataCellStyle(colIdx)"
                  @click="onHeaderClick(colIdx)"
                  @mouseenter="!isAllDone && (hoveredCol = colIdx)"
                  @mouseleave="hoveredCol = null"
                >
                  <span class="cell-value">{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Fade right edge (scroll hint) -->
        <div class="table-fade-right"></div>
      </div>

      <!-- ── Footer actions ── -->
      <div class="wizard-footer">
        <div class="wizard-footer-left">
          <button
            v-if="currentStepIdx > 0 && !isAllDone"
            class="btn-ghost"
            @click="prevStep"
          >
            <v-icon small class="mr-1">mdi-arrow-left</v-icon>
            Paso anterior
          </button>
        </div>
        <div class="wizard-footer-right">
          <button
            v-if="!isAllDone && currentField && currentField.skippable"
            class="btn-ghost"
            @click="skipStep"
          >
            Saltar
            <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>

      <!-- ── Summary (all done) ── -->
      <div v-if="isAllDone" class="mapping-summary mt-4">
        <p class="summary-title">Mapeo configurado</p>
        <div class="summary-chips">
          <div
            v-for="field in fieldSteps"
            :key="field.key"
            class="summary-chip"
            :class="mappings[field.key] != null ? 'chip-mapped' : 'chip-empty'"
            :style="mappings[field.key] != null ? {
              background: fieldHexLight(field.key),
              borderColor: fieldHex(field.key),
              color: fieldHex(field.key),
            } : {}"
          >
            <span
              v-if="mappings[field.key] != null"
              class="summary-chip-dot"
              :style="{ background: fieldHex(field.key) }"
            ></span>
            <span class="summary-chip-field">{{ fieldLabel(field.key) }}</span>
            <span class="summary-chip-arrow">→</span>
            <span class="summary-chip-col">
              {{ mappings[field.key] != null ? headers[mappings[field.key]] : '—' }}
            </span>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import { parseRawFile } from "../../../../utils/parseRawFile";
import { normalizarFila } from "../../../../utils/normalizeImportValues";

// ── Color system ──────────────────────────────────────────────
const FIELD_HEX = {
  codigoBarrasProveedor: "#00897B",
  nombre:                "#3949AB",
  precioCompra:          "#E53935",
  descuentoProveedor:    "#8E24AA",
  marca:                 "#1E88E5",
  modelo:                "#2E7D32",
  tamanio:               "#F57C00",
  otrasCaracteristicas:  "#5D4037",
};

const FIELD_HEX_LIGHT = {
  codigoBarrasProveedor: "#E0F2F1",
  nombre:                "#E8EAF6",
  precioCompra:          "#FFEBEE",
  descuentoProveedor:    "#F3E5F5",
  marca:                 "#E3F2FD",
  modelo:                "#E8F5E9",
  tamanio:               "#FFF3E0",
  otrasCaracteristicas:  "#EFEBE9",
};

const FIELD_STEPS_SIMPLE = [
  { key: "codigoBarrasProveedor", required: false, skippable: true },
  { key: "nombre",                required: false, skippable: true },
  { key: "precioCompra",          required: true,  skippable: false },
  { key: "descuentoProveedor",    required: false, skippable: true },
];

const FIELD_STEPS_ESTRUCTURADO = [
  { key: "codigoBarrasProveedor", required: false, skippable: true },
  { key: "marca",                 required: false, skippable: true },
  { key: "modelo",                required: false, skippable: true },
  { key: "tamanio",               required: false, skippable: true },
  { key: "otrasCaracteristicas",  required: false, skippable: true },
  { key: "precioCompra",          required: true,  skippable: false },
  { key: "descuentoProveedor",    required: false, skippable: true },
];

const PREVIEW_ROWS = 10;

export default {
  name: "PasoMapeoColumnas",
  props: {
    modo: { type: String, required: true },
  },
  data() {
    return {
      file: null,
      headers: [],
      allRows: [],
      previewRows: [],
      loadingFile: false,
      parseError: null,
      currentStepIdx: 0,
      mappings: {},
      hoveredCol: null,
      colWidths: [],
    };
  },
  computed: {
    hasFile() {
      return this.headers.length > 0;
    },
    fieldSteps() {
      return this.modo === "ESTRUCTURADO"
        ? FIELD_STEPS_ESTRUCTURADO
        : FIELD_STEPS_SIMPLE;
    },
    currentField() {
      return this.fieldSteps[this.currentStepIdx] || null;
    },
    isAllDone() {
      return this.currentStepIdx >= this.fieldSteps.length;
    },
  },
  methods: {
    // ── Color helpers ──
    fieldHex(key) {
      return FIELD_HEX[key] || "#757575";
    },
    fieldHexLight(key) {
      return FIELD_HEX_LIGHT[key] || "#F5F5F5";
    },

    // ── File handling ──
    async onFileSelected(fileOrEvent) {
      const f =
        fileOrEvent instanceof File
          ? fileOrEvent
          : Array.isArray(this.file)
          ? this.file[0]
          : this.file;
      if (!f || !(f instanceof File)) return;

      this.loadingFile = true;
      this.parseError = null;
      this.resetMappings();

      try {
        const { headers, rows } = await parseRawFile(f);
        this.headers = headers;
        this.allRows = rows;
        this.previewRows = rows.slice(0, PREVIEW_ROWS);
        this.colWidths = this._computeColWidths(headers, rows);
        this.currentStepIdx = 0;
      } catch (e) {
        this.parseError = e.message || this.$t("importarProductos.errorProcesar");
        this.$emit("error", this.parseError);
      } finally {
        this.loadingFile = false;
      }
    },

    resetFile() {
      this.file = null;
      this.headers = [];
      this.allRows = [];
      this.previewRows = [];
      this.parseError = null;
      this.resetMappings();
    },
    resetMappings() {
      this.mappings = {};
      this.currentStepIdx = 0;
      this.hoveredCol = null;
      this.colWidths = [];
    },

    // ── Wizard navigation ──
    onHeaderClick(colIdx) {
      if (this.isAllDone || !this.currentField) return;
      const alreadyUsedByOther = Object.entries(this.mappings).some(
        ([k, v]) => v === colIdx && k !== this.currentField.key
      );
      if (alreadyUsedByOther) {
        this.$emit("error", this.$t("importarProductos.columnAlreadyMapped"));
        return;
      }
      this.$set(this.mappings, this.currentField.key, colIdx);
      this.hoveredCol = null;
      this.currentStepIdx++;
    },
    skipStep() {
      if (!this.currentField || !this.currentField.skippable) return;
      this.$delete(this.mappings, this.currentField.key);
      this.currentStepIdx++;
    },
    prevStep() {
      if (this.currentStepIdx === 0) return;
      this.currentStepIdx--;
      if (this.currentField) {
        this.$delete(this.mappings, this.currentField.key);
      }
    },

    // ── Column lookup ──
    mappedFieldForCol(colIdx) {
      const entry = Object.entries(this.mappings).find(([, v]) => v === colIdx);
      return entry ? entry[0] : null;
    },

    // ── Table style helpers ──
    _computeColWidths(headers, rows) {
      const preview = rows.slice(0, PREVIEW_ROWS);
      return headers.map((h, i) => {
        const hLen = (h || "(sin nombre)").length;
        const maxData = preview.reduce((m, r) => Math.max(m, String(r[i] ?? "").length), 0);
        const w = Math.max(hLen * 6.5, maxData * 7) + 60;
        return Math.min(280, Math.max(130, w));
      });
    },
    headerColClass(colIdx) {
      const mapped = this.mappedFieldForCol(colIdx) != null;
      const isHovered = this.hoveredCol === colIdx && !this.isAllDone && !mapped;
      const isActive = !this.isAllDone;
      return {
        "col-header-mapped":    mapped,
        "col-header-hovered":   isHovered,
        "col-header-clickable": isActive && !mapped,
      };
    },
    headerColStyle(colIdx) {
      const mappedKey = this.mappedFieldForCol(colIdx);
      const w = this.colWidths[colIdx];
      const fixedW = w ? { width: w + "px", minWidth: w + "px", maxWidth: w + "px" } : {};
      if (mappedKey) {
        return { ...fixedW, background: this.fieldHexLight(mappedKey), cursor: "default" };
      }
      if (this.hoveredCol === colIdx && !this.isAllDone) {
        const activeKey = this.currentField && this.currentField.key;
        return { ...fixedW, background: activeKey ? this.fieldHexLight(activeKey) : "#EEF5FF", cursor: "pointer" };
      }
      return { ...fixedW, cursor: this.isAllDone ? "default" : "pointer" };
    },
    dataCellClass(colIdx) {
      return {
        "cell-col-mapped":  this.mappedFieldForCol(colIdx) != null,
        "cell-col-hovered": this.hoveredCol === colIdx && !this.isAllDone,
      };
    },
    dataCellStyle(colIdx) {
      const mappedKey = this.mappedFieldForCol(colIdx);
      const cursor = this.isAllDone || mappedKey ? "default" : "pointer";
      if (mappedKey) {
        return { background: this.fieldHexLight(mappedKey) + "66", cursor };
      }
      if (this.hoveredCol === colIdx && !this.isAllDone) {
        const activeKey = this.currentField && this.currentField.key;
        return { background: activeKey ? this.fieldHexLight(activeKey) + "99" : "#EEF5FF99", cursor };
      }
      return { cursor };
    },

    // ── i18n ──
    fieldLabel(key) {
      const map = {
        codigoBarrasProveedor: this.$t("importarProductos.fieldCodigoBarrasProveedor"),
        nombre:                this.$t("importarProductos.fieldNombre"),
        precioCompra:          this.$t("importarProductos.fieldPrecioCompra"),
        descuentoProveedor:    this.$t("importarProductos.fieldDescuentoProveedor"),
        marca:                 this.$t("importarProductos.fieldMarca"),
        modelo:                this.$t("importarProductos.fieldModelo"),
        tamanio:               this.$t("importarProductos.fieldTamanio"),
        otrasCaracteristicas:  this.$t("importarProductos.fieldOtrasCaracteristicas"),
      };
      return map[key] || key;
    },

    // ── Public ──
    procesar() {
      if (!this.hasFile) {
        this.$emit("error", this.$t("importarProductos.errorSeleccion"));
        return null;
      }
      if (this.mappings.precioCompra == null) {
        this.$emit("error", this.$t("importarProductos.errorPrecioRequired"));
        return null;
      }
      if (this.modo === "SIMPLE") {
        if (
          this.mappings.codigoBarrasProveedor == null &&
          this.mappings.nombre == null
        ) {
          this.$emit("error", this.$t("importarProductos.errorIdentificadorRequired"));
          return null;
        }
      } else {
        if (
          this.mappings.marca == null &&
          this.mappings.modelo == null &&
          this.mappings.tamanio == null
        ) {
          this.$emit("error", this.$t("importarProductos.errorIdentificadorEstructurado"));
          return null;
        }
      }

      const m = this.mappings;
      const skipped = [];

      const rows = this.allRows
        .map((row, rowIdx) => {
          const raw = {};

          raw.precioCompra = m.precioCompra != null ? row[m.precioCompra] : null;
          raw.codigoBarrasProveedor =
            m.codigoBarrasProveedor != null ? row[m.codigoBarrasProveedor] : null;

          if (m.nombre != null) raw.nombre = row[m.nombre];

          raw.descuentoProveedor =
            m.descuentoProveedor != null ? row[m.descuentoProveedor] : 0;

          if (this.modo === "ESTRUCTURADO") {
            for (const k of ["marca", "modelo", "tamanio", "otrasCaracteristicas"]) {
              if (m[k] != null) raw[k] = row[m[k]];
            }
          }

          const item = normalizarFila(raw);

          if (!item.precioCompra || item.precioCompra <= 0) {
            skipped.push(rowIdx + 2);
            return null;
          }

          // En modo ESTRUCTURADO, construir nombre desde partes si no viene mapeado
          if (this.modo === "ESTRUCTURADO" && !item.nombre) {
            const partes = ["marca", "modelo", "tamanio", "otrasCaracteristicas"]
              .map((k) => item[k])
              .filter(Boolean);
            if (partes.length) item.nombre = partes.join(" ");
          }

          return item;
        })
        .filter(Boolean);

      if (rows.length === 0) {
        this.$emit("error", this.$t("importarProductos.errorFilasVacias"));
        return null;
      }

      if (skipped.length > 0) {
        const preview = skipped.slice(0, 5).join(", ");
        const extra = skipped.length > 5 ? ` y ${skipped.length - 5} mas` : "";
        this.$emit(
          "error",
          `Se omitieron ${skipped.length} fila(s) sin precio valido (filas: ${preview}${extra}). El resto se procesara normalmente.`
        );
      }

      return rows;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   UPLOAD ZONE
═══════════════════════════════════════ */
.upload-zone {
  padding: 8px 0 4px;
}
.upload-input {
  max-width: 520px;
}

/* ═══════════════════════════════════════
   FILE BAR
═══════════════════════════════════════ */
.file-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #F8F9FA;
  border: 1px solid #E8EAED;
  border-radius: 8px;
}
.file-bar-info {
  display: flex;
  align-items: center;
}
.file-bar-name {
  font-size: 13px;
  font-weight: 500;
  color: #202124;
}
.file-bar-count {
  font-size: 12px;
  color: #80868B;
}
.file-bar-change {
  font-size: 12px;
  color: #5F6368;
  cursor: pointer;
  background: none;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: background 0.15s;
}
.file-bar-change:hover {
  background: #EAEAEA;
}

/* ═══════════════════════════════════════
   INSTRUCTION BANNER
═══════════════════════════════════════ */
.instruction-banner {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13.5px;
  line-height: 1.4;
  border-width: 1px;
  border-style: solid;
}
.banner-active {
  background: #EBF4FF;
  border-color: #BBDEFB;
  color: #1565C0;
}
.banner-done {
  background: #F1F8E9;
  border-color: #DCEDC8;
  color: #2E7D32;
}
.banner-step-badge {
  flex-shrink: 0;
  background: #1976D2;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  margin-right: 10px;
}
.banner-text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.banner-verb { color: #1565C0; }
.banner-field {
  font-weight: 700;
  font-size: 14px;
}
.banner-req {
  font-size: 11px;
  color: #E53935;
  background: #FFEBEE;
  border-radius: 4px;
  padding: 1px 5px;
}
.banner-opt {
  font-size: 11px;
  color: #757575;
}

/* ═══════════════════════════════════════
   TABLE WRAPPER
═══════════════════════════════════════ */
.table-wrapper {
  position: relative;
  border: 1px solid #E8EAED;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.table-scroll {
  overflow-x: auto;
}
.table-fade-right {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 32px;
  background: linear-gradient(to right, transparent, rgba(255,255,255,0.9));
  pointer-events: none;
}

/* ═══════════════════════════════════════
   TABLE ITSELF
═══════════════════════════════════════ */
.mapping-table {
  border-collapse: collapse;
  table-layout: fixed;
  width: auto;
  min-width: 100%;
  font-size: 13px;
}

/* Row number column */
.col-num {
  width: 36px;
  min-width: 36px;
  text-align: center;
  background: #F8F9FA;
  color: #BDBDBD;
  font-size: 11px;
  border-right: 1px solid #F0F0F0;
  padding: 0 6px;
  user-select: none;
}
thead .col-num {
  height: 44px;
  vertical-align: middle;
  border-bottom: 2px solid #E8EAED;
}
tbody .col-num {
  border-bottom: 1px solid #F5F5F5;
}

/* Header columns */
.col-header {
  position: relative;
  vertical-align: top;
  padding: 0;
  background: #F8F9FA;
  border-right: 1px solid #F0F0F0;
  border-bottom: 2px solid #E8EAED;
  transition: background 0.15s ease;
  user-select: none;
}
.col-header:last-child { border-right: none; }
.col-header-clickable { cursor: pointer; }

/* Color bar — top stripe of column */
.col-color-bar {
  height: 3px;
  width: 100%;
  transition: background 0.15s;
}
.col-color-bar-hover {
  background: #1976D2 !important;
  opacity: 0.5;
}

.col-header-inner {
  display: flex;
  flex-direction: column;
}
.col-header-content {
  padding: 6px 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.col-header-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-height: 40px;
}
.col-header-name {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5F6368;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

/* Field tag badge */
.field-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10.5px;
  font-weight: 600;
  border-width: 1px;
  border-style: solid;
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.field-tag-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.field-tag-hover {
  opacity: 0.85;
  animation: tag-fade-in 0.15s ease;
}
@keyframes tag-fade-in {
  from { opacity: 0; transform: translateY(-2px); }
  to   { opacity: 0.85; transform: translateY(0); }
}

/* Data rows */
.data-row { transition: background 0.1s; }
.row-even { background: #FFFFFF; }
.row-odd  { background: #FAFAFA; }

.data-cell {
  padding: 6px 10px;
  border-right: 1px solid #F0F0F0;
  border-bottom: 1px solid #F5F5F5;
  color: #3C4043;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.12s ease;
  font-size: 12.5px;
}
.data-cell:last-child { border-right: none; }
.cell-value {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ═══════════════════════════════════════
   WIZARD FOOTER
═══════════════════════════════════════ */
.wizard-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.wizard-footer-left,
.wizard-footer-right {
  display: flex;
  align-items: center;
}
.btn-ghost {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  color: #5F6368;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
}
.btn-ghost:hover {
  background: #F1F3F4;
  color: #202124;
}

/* ═══════════════════════════════════════
   SUMMARY
═══════════════════════════════════════ */
.mapping-summary {
  border-top: 1px solid #F0F0F0;
  padding-top: 12px;
}
.summary-title {
  font-size: 12px;
  font-weight: 600;
  color: #5F6368;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}
.summary-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  border-width: 1px;
  border-style: solid;
}
.chip-mapped { font-weight: 500; }
.chip-empty {
  background: #F5F5F5;
  border-color: #E0E0E0;
  color: #9E9E9E;
}
.summary-chip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.summary-chip-field { font-weight: 600; }
.summary-chip-arrow { opacity: 0.4; font-size: 11px; }
.summary-chip-col {
  font-family: monospace;
  font-size: 11.5px;
  font-weight: 400;
}
</style>
